import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { LeaveModel } from "../../api/leave/LeaveModel";
import { LeaveAdapter } from "../../api/leave/LeaveAdapter";
import type {
  LeaveRequest,
  LeaveType,
  CreateLeaveRequest,
  UpdateLeaveRequest,
  LeaveRequestsQuery,
  UserLeaveTypeBalance,
  SetLeaveTypeBalancePayload,
} from "../../api/leave/Leave.typed";
import type { PaginatedResponse } from "../../api/zekay/pagination";

interface LeaveContextType {
  createLeaveRequest: (request: CreateLeaveRequest) => Promise<LeaveRequest | undefined>;
  getLeaveRequests: (
    query?: LeaveRequestsQuery,
  ) => Promise<PaginatedResponse<LeaveRequest> | undefined>;
  getLeaveRequest: (id: number) => Promise<LeaveRequest | undefined>;
  updateLeaveRequest: (id: number, update: UpdateLeaveRequest) => Promise<LeaveRequest | undefined>;
  deleteLeaveRequest: (id: number) => Promise<void>;
  listLeaveTypes: () => Promise<LeaveType[] | undefined>;
  getLeaveType: (id: number) => Promise<LeaveType | undefined>;
  createLeaveType: (payload: Omit<LeaveType, "ID">) => Promise<{ ID: number } | undefined>;
  updateLeaveType: (id: number, payload: Omit<LeaveType, "ID">) => Promise<void>;
  deleteLeaveType: (id: number) => Promise<void>;
  listLeaveTypeBalances: (
    typeId: number,
    page?: number,
    limit?: number,
  ) => Promise<PaginatedResponse<UserLeaveTypeBalance> | undefined>;
  listBalancesByUser: (
    userId: number,
    page?: number,
    limit?: number,
  ) => Promise<PaginatedResponse<UserLeaveTypeBalance> | undefined>;
  setLeaveTypeBalance: (
    typeId: number,
    userId: number,
    payload: SetLeaveTypeBalancePayload,
  ) => Promise<void>;
  deleteLeaveTypeBalance: (typeId: number, userId: number) => Promise<void>;
  incrementLeaveTypeBalance: (
    typeId: number,
    userId: number,
    payload: { Delta: number },
  ) => Promise<void>;
}

const LeaveContext = React.createContext<LeaveContextType>(null!);

export function LeaveProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new LeaveModel(appConfig);
    return new LeaveAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const createLeaveRequest = React.useCallback(
    async (request: CreateLeaveRequest): Promise<LeaveRequest | undefined> => {
      if (!adapter) return undefined;
      try {
        return await adapter.createLeaveRequest(request);
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [adapter, showAlert],
  );

  const getLeaveRequests = React.useCallback(
    async (query?: LeaveRequestsQuery): Promise<PaginatedResponse<LeaveRequest> | undefined> =>
      adapter ? wrap(() => adapter.getLeaveRequests(query)) : undefined,
    [adapter, wrap],
  );

  const getLeaveRequest = React.useCallback(
    async (id: number): Promise<LeaveRequest | undefined> =>
      adapter ? wrap(() => adapter.getLeaveRequest(id)) : undefined,
    [adapter, wrap],
  );

  const updateLeaveRequest = React.useCallback(
    async (id: number, update: UpdateLeaveRequest): Promise<LeaveRequest | undefined> =>
      adapter ? wrap(() => adapter.updateLeaveRequest(id, update)) : undefined,
    [adapter, wrap],
  );

  const deleteLeaveRequest = React.useCallback(
    async (id: number): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.deleteLeaveRequest(id);
        showAlert("Demande supprimée", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const listLeaveTypes = React.useCallback(
    async (): Promise<LeaveType[] | undefined> =>
      adapter ? wrap(() => adapter.listLeaveTypes()) : undefined,
    [adapter, wrap],
  );

  const getLeaveType = React.useCallback(
    async (id: number): Promise<LeaveType | undefined> =>
      adapter ? wrap(() => adapter.getLeaveType(id)) : undefined,
    [adapter, wrap],
  );

  const createLeaveType = React.useCallback(
    async (payload: Omit<LeaveType, "ID">): Promise<{ ID: number } | undefined> =>
      adapter ? wrap(() => adapter.createLeaveType(payload)) : undefined,
    [adapter, wrap],
  );

  const updateLeaveType = React.useCallback(
    async (id: number, payload: Omit<LeaveType, "ID">): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateLeaveType(id, payload);
        showAlert("Type de congé mis à jour", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const deleteLeaveType = React.useCallback(
    async (id: number): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.deleteLeaveType(id);
        showAlert("Type de congé supprimé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const listLeaveTypeBalances = React.useCallback(
    async (
      typeId: number,
      page?: number,
      limit?: number,
    ): Promise<PaginatedResponse<UserLeaveTypeBalance> | undefined> =>
      adapter ? wrap(() => adapter.listLeaveTypeBalances(typeId, page, limit)) : undefined,
    [adapter, wrap],
  );

  const listBalancesByUser = React.useCallback(
    async (
      userId: number,
      page?: number,
      limit?: number,
    ): Promise<PaginatedResponse<UserLeaveTypeBalance> | undefined> =>
      adapter ? wrap(() => adapter.listBalancesByUser(userId, page, limit)) : undefined,
    [adapter, wrap],
  );

  const setLeaveTypeBalance = React.useCallback(
    async (typeId: number, userId: number, payload: SetLeaveTypeBalancePayload): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.setLeaveTypeBalance(typeId, userId, payload);
        showAlert("Solde mis à jour", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const deleteLeaveTypeBalance = React.useCallback(
    async (typeId: number, userId: number): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.deleteLeaveTypeBalance(typeId, userId);
        showAlert("Solde supprimé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const incrementLeaveTypeBalance = React.useCallback(
    async (typeId: number, userId: number, payload: { Delta: number }): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.incrementLeaveTypeBalance(typeId, userId, payload);
        showAlert("Solde mis à jour", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const value = React.useMemo<LeaveContextType>(
    () => ({
      createLeaveRequest,
      getLeaveRequests,
      getLeaveRequest,
      updateLeaveRequest,
      deleteLeaveRequest,
      listLeaveTypes,
      getLeaveType,
      createLeaveType,
      updateLeaveType,
      deleteLeaveType,
      listLeaveTypeBalances,
      listBalancesByUser,
      setLeaveTypeBalance,
      deleteLeaveTypeBalance,
      incrementLeaveTypeBalance,
    }),
    [
      createLeaveRequest,
      getLeaveRequests,
      getLeaveRequest,
      updateLeaveRequest,
      deleteLeaveRequest,
      listLeaveTypes,
      getLeaveType,
      createLeaveType,
      updateLeaveType,
      deleteLeaveType,
      listLeaveTypeBalances,
      listBalancesByUser,
      setLeaveTypeBalance,
      deleteLeaveTypeBalance,
      incrementLeaveTypeBalance,
    ],
  );

  return <LeaveContext.Provider value={value}>{props.children}</LeaveContext.Provider>;
}

export function useLeave(): LeaveContextType {
  const ctx = React.useContext(LeaveContext);
  if (!ctx) throw new Error("useLeave must be used within LeaveProvider");
  return ctx;
}
